// SCR-STAFF-201 تب «کد ملی و مالیات» (AP-CAT-02): کد ملی از کتاب ارزش نسبی جاری (US-CAT-003، AC-CAT-003.1) یا خدمت آزاد
// (AC-CAT-003.2)، اجزای حرفه‌ای و فنی به درصد یا مبلغ (AC-CAT-003.3)، و پروفایل مالیاتی و شناسه مودیان (US-CAT-004؛ cat.tax.manage).
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type NationalCodeView, type ServiceSetupView, type ServiceView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  ErrorState,
  faDigits,
  LoadingState,
  MoneyFieldRHF,
  SampleBadge,
  SelectRHF,
  TextFieldRHF,
} from '@phoenix/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDeferredValue, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { catKeys, fromBasisPoints, SavedNotice, toBasisPoints, useTaxProfiles } from './common';

const codingSchema = z
  .object({
    nationalCode: z.string().nullable(),
    componentMode: z.enum(['None', 'Percent', 'Amount']),
    professionalPercent: z.string().optional(),
    professionalAmount: z.number().int().min(0).optional(),
  })
  .superRefine((value, ctx) => {
    const percent = value.professionalPercent?.trim() ? Number(value.professionalPercent) : undefined;
    if (
      value.componentMode === 'Percent' &&
      (percent === undefined || Number.isNaN(percent) || percent < 0 || percent > 100)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['professionalPercent'],
        message: 'errors:validation.components',
      });
    }
    if (value.componentMode === 'Amount' && value.professionalAmount === undefined) {
      ctx.addIssue({ code: 'custom', path: ['professionalAmount'], message: 'common:validation.required' });
    }
  });
type CodingForm = z.input<typeof codingSchema>;

const taxSchema = z.object({
  taxProfileId: z.string(),
  moadianCode: z
    .string()
    .trim()
    .refine((value) => value === '' || /^[0-9]{13}$/.test(value), {
      message: 'errors:validation.moadian_code',
    }),
});
type TaxForm = z.input<typeof taxSchema>;

/** نمایش یک عدد ساده از داده سطر کتاب (واحدها). */
function bookNumber(row: NationalCodeView | null | undefined, key: string): string {
  const data = row?.data as Record<string, unknown> | null | undefined;
  const value = data?.[key];
  return typeof value === 'number' || typeof value === 'string' ? faDigits(value) : '—';
}

function CodingSection({ service, setup }: { service: ServiceView; setup: ServiceSetupView }) {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cat.service.manage');
  const [search, setSearch] = useState('');
  const deferred = useDeferredValue(search.trim());
  const [saved, setSaved] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();
  // گزینه انتخاب‌شده جدا از نتایج جستجو نگه داشته می‌شود تا تغییر نتایج، مقدار را تهی و ورودی را دوباره تنظیم نکند.
  const [selected, setSelected] = useState<NationalCodeView | null>(setup.nationalCodeInfo ?? null);

  const codes = useQuery({
    queryKey: catKeys.nationalCodes(session?.tenantId, deferred),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/catalog/national-codes', { params: { query: { q: deferred || undefined } } }),
      ),
    placeholderData: keepPreviousData,
  });

  const values = useMemo<CodingForm>(
    () => ({
      nationalCode: setup.nationalCode ?? null,
      componentMode: setup.componentMode,
      professionalPercent: fromBasisPoints(setup.professionalShareBasisPoints)?.toString() ?? '',
      professionalAmount: setup.professionalAmount ? Number(setup.professionalAmount.amount) : undefined,
    }),
    [setup],
  );
  const { control, handleSubmit, formState, setError } = useForm<CodingForm>({
    resolver: zodResolver(codingSchema),
    values,
  });
  const mode = useWatch({ control, name: 'componentMode' });

  const save = useMutation({
    mutationFn: (form: CodingForm) =>
      unwrap(
        tenantApi.PUT('/api/v1/catalog/services/{id}/coding', {
          params: { path: { id: service.id } },
          body: {
            nationalCode: form.nationalCode || null,
            componentMode: form.componentMode,
            professionalShareBasisPoints:
              form.componentMode === 'Percent' && form.professionalPercent
                ? toBasisPoints(Number(form.professionalPercent))
                : null,
            professionalAmount:
              form.componentMode === 'Amount' && form.professionalAmount !== undefined
                ? { amount: form.professionalAmount, currency: 'IRR' }
                : null,
          },
        }),
      ),
    onMutate: () => {
      setSaved(false);
      setSubmitError(undefined);
    },
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['nationalCode'],
        codeFields: { 'PHX-CAT-0206': 'nationalCode' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  const found = codes.data?.items ?? [];
  const options =
    selected && !found.some((option) => option.code === selected.code) ? [selected, ...found] : found;
  const book = setup.nationalCodeInfo;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        component="form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (write.canWrite) {
            void handleSubmit((form) => save.mutate(form))();
          }
        }}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle1">{t('cat:coding.title')}</Typography>
          <WriteNotice write={write} />
          <SavedNotice show={saved && !formState.isDirty} />
          {setup.nationalCodeReviewRequired && (
            <Alert severity="warning" data-testid="coding-review">
              {t('cat:coding.review_required')}
            </Alert>
          )}
          <Controller
            control={control}
            name="nationalCode"
            render={({ field, fieldState }) => (
              <Autocomplete
                options={options}
                value={field.value && selected?.code === field.value ? selected : null}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') {
                    setSearch(value);
                  }
                }}
                onChange={(_, option) => {
                  setSelected(option);
                  setSearch('');
                  field.onChange(option?.code ?? null);
                }}
                getOptionLabel={(option) => `${option.code} — ${option.title ?? ''}`}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                noOptionsText={t('cat:coding.no_codes')}
                disabled={!write.canWrite}
                renderOption={(props, option) => (
                  <li {...props} key={option.code}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <bdi dir="ltr">{option.code}</bdi>
                      <span>{option.title}</span>
                      {option.isSample && <SampleBadge />}
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('cat:coding.national_code')}
                    error={Boolean(fieldState.error)}
                    helperText={
                      fieldState.error
                        ? t(fieldState.error.message ?? '')
                        : t('cat:coding.national_code_help')
                    }
                    slotProps={{ htmlInput: { ...params.inputProps, 'data-testid': 'coding-national-code' } }}
                  />
                )}
              />
            )}
          />
          <Stack direction="row" spacing={1} alignItems="center" data-testid="coding-book">
            {setup.outOfTariffBook ? (
              <Chip size="small" variant="outlined" label={t('cat:coding.out_of_book')} />
            ) : (
              <>
                <Chip size="small" variant="outlined" label={book?.title ?? setup.nationalCode} />
                <Typography variant="body2" color="text.secondary">
                  {t('cat:coding.units', {
                    professional: bookNumber(book, 'professional_units'),
                    technical: bookNumber(book, 'technical_units'),
                    version: faDigits(setup.nationalCodeBookVersion ?? 0),
                  })}
                </Typography>
                {book?.isSample && <SampleBadge />}
              </>
            )}
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <SelectRHF
              control={control}
              name="componentMode"
              labelKey="cat:coding.component_mode"
              helperKey="cat:coding.component_help"
              fullWidth
              disabled={!write.canWrite}
              options={(['None', 'Percent', 'Amount'] as const).map((value) => ({
                value,
                labelKey: `cat:coding.modes.${value}`,
              }))}
            />
            {mode === 'Percent' && (
              <TextFieldRHF
                control={control}
                name="professionalPercent"
                labelKey="cat:coding.professional_percent"
                type="number"
                required
                fullWidth
                disabled={!write.canWrite}
                slotProps={{ htmlInput: { min: 0, max: 100, step: 0.01, 'data-testid': 'coding-percent' } }}
              />
            )}
            {mode === 'Amount' && (
              <MoneyFieldRHF
                control={control}
                name="professionalAmount"
                labelKey="cat:coding.professional_amount"
                required
                fullWidth
                disabled={!write.canWrite}
              />
            )}
          </Stack>
        </Stack>
        {submitError ? <ErrorState error={submitError} compact /> : null}
        <Button
          type="submit"
          variant="contained"
          sx={{ mt: 2 }}
          disabled={!write.canWrite || save.isPending}
          data-testid="coding-submit"
        >
          {t('common:actions.save')}
        </Button>
      </Box>
    </Paper>
  );
}

function TaxSection({ service, setup }: { service: ServiceView; setup: ServiceSetupView }) {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cat.tax.manage');
  const profiles = useTaxProfiles();
  const [saved, setSaved] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();

  const values = useMemo<TaxForm>(
    () => ({ taxProfileId: setup.taxProfileId ?? '', moadianCode: setup.moadianCode ?? '' }),
    [setup],
  );
  const { control, handleSubmit, formState, setError } = useForm<TaxForm>({
    resolver: zodResolver(taxSchema),
    values,
  });

  const save = useMutation({
    mutationFn: (form: TaxForm) =>
      unwrap(
        tenantApi.PUT('/api/v1/catalog/services/{id}/tax', {
          params: { path: { id: service.id } },
          body: { taxProfileId: form.taxProfileId || null, moadianCode: form.moadianCode.trim() || null },
        }),
      ),
    onMutate: () => {
      setSaved(false);
      setSubmitError(undefined);
    },
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['taxProfileId', 'moadianCode'],
        codeFields: { 'PHX-CAT-0502': 'taxProfileId' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  const active = (profiles.data?.items ?? []).filter((profile) => profile.isActive);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        component="form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (write.canWrite) {
            void handleSubmit((form) => save.mutate(form))();
          }
        }}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle1">{t('cat:tax.title')}</Typography>
          <WriteNotice write={write} />
          <SavedNotice show={saved && !formState.isDirty} />
          <Typography variant="body2" data-testid="tax-effective">
            {t('cat:tax.effective', { category: t(`cat:tax_category.${setup.effectiveTaxCategory}`) })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('cat:tax.suggested', { category: t(`cat:tax_category.${setup.suggestedTaxCategory}`) })}
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <SelectRHF
              control={control}
              name="taxProfileId"
              labelKey="cat:tax.profile"
              helperKey="cat:tax.profile_help"
              fullWidth
              disabled={!write.canWrite}
              options={[
                { value: '', labelKey: 'cat:tax.profile_default' },
                ...active.map((profile) => ({
                  value: profile.id,
                  label: `${profile.name} (${t(`cat:tax_category.${profile.category}`)})`,
                })),
              ]}
            />
            <TextFieldRHF
              control={control}
              name="moadianCode"
              labelKey="cat:tax.moadian_code"
              helperKey="cat:tax.moadian_help"
              fullWidth
              disabled={!write.canWrite}
              slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'tax-moadian' } }}
            />
          </Stack>
        </Stack>
        {submitError ? <ErrorState error={submitError} compact /> : null}
        <Button
          type="submit"
          variant="contained"
          sx={{ mt: 2 }}
          disabled={!write.canWrite || save.isPending}
          data-testid="tax-submit"
        >
          {t('common:actions.save')}
        </Button>
      </Box>
    </Paper>
  );
}

/** تب کد ملی و مالیات. */
export function CodingTab({ service }: { service: ServiceView }) {
  const { tenantApi, session } = useTenantSession();
  const setup = useQuery({
    queryKey: catKeys.setup(session?.tenantId, service.id),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/catalog/services/{id}/setup', { params: { path: { id: service.id } } })),
  });

  if (setup.isPending) {
    return <LoadingState rows={3} />;
  }
  if (setup.error) {
    return <ErrorState error={setup.error} onRetry={() => void setup.refetch()} />;
  }

  return (
    <Stack spacing={2}>
      <CodingSection service={service} setup={setup.data} />
      <TaxSection service={service} setup={setup.data} />
    </Stack>
  );
}
